/**
 * @file index.tsx
 *
 * @fileoverview Renders a drawer in a position defined by props. Can be used for sidenavs or mobile drops.
 */
import { cva, VariantProps } from "class-variance-authority";
import FocusTrap from "focus-trap-react";
import { motion, MotionProps } from "framer-motion";
import { ReactNode, useEffect, useState } from "react";
import HiddenVisually from "../../../02-atoms/14-w-a11y/02-visually-hidden";
import styles from "./drawer.module.scss";
// Define a new interface that extends MotionProps
type MotionDivProps = MotionProps & {
  className?: string;
  onKeyDown?: any;
};
const CustomMotion = motion<MotionDivProps>("div");

const ESCAPE_KEY = 27;

const variants = {
  left: {
    open: {
      x: "0%",
      opacity: 1,
      transition: { ease: "easeIn", duration: 0.3, staggerChildren: 0.3 },
    },
    closed: {
      x: "-150%",
      opacity: 0,
      transition: { ease: "easeOut", duration: 0.3, staggerChildren: 0.3 },
    },
  },
  right: {
    open: {
      x: "0%",
      opacity: 1,
      transition: { ease: "easeIn", duration: 0.3, staggerChildren: 0.3 },
    },
    closed: {
      x: "200%",
      opacity: 0,
      transition: { ease: "easeOut", duration: 0.3, staggerChildren: 0.3 },
    },
  },
  bottom: {
    open: {
      y: "0%",
      opacity: 1,
      transition: { ease: "easeIn", duration: 0.2, staggerChildren: 0.2 },
    },
    closed: {
      y: "100%",
      opacity: 0,
      transition: { ease: "easeOut", duration: 0.2, staggerChildren: 0.2 },
    },
  },
  top: {
    open: {
      y: "0%",
      opacity: 1,
      transition: { ease: "easeIn", duration: 0.3, staggerChildren: 0.3 },
    },
    closed: {
      y: "-100%",
      opacity: 0,
      transition: { ease: "easeOut", duration: 0.3, staggerChildren: 0.3 },
    },
  },
};

const sideNavBlock = cva([styles.sideNavBlock], {
  variants: {
    position: {
      left: styles.sideNavBlockPositionLeft,
      right: styles.sideNavBlockPositionRight,
      bottom: styles.sideNavBlockPositionBottom,
      top: styles.sideNavBlockPositionTop,
    },
    full: {
      true: styles.sideNavBlockFull,
    },
  },
  defaultVariants: {
    position: "left",
    full: false,
  },
});

export interface DrawerProps extends VariantProps<typeof sideNavBlock> {
  /**
   * The contents of the drawer.
   */
  children: ReactNode;
  /**
   * If the drawer is open.
   */
  isOpen: boolean;
  /**
   * What to do when the drawer is toggled.
   */
  onToggle: any;
  /**
   * If a fixed height should be used.
   */
  height?: string;
  /**
   * If a fixed max width should be used.
   */
  maxWidth?: string;
  /**
   * If dragging is enabled. Dragging might affect ability to scroll.
   *
   * @default "true".
   */
  allowDrag?: boolean;
}

/**
 * Handle keyboard escape manually because we want to hide the navbar when key is pressed. Not only
 * remove the focus trap.
 *
 * @event {SyntheticKeyboardEvent}
 *  The event called by the keydown.
 */
const handleKeyDown = (event, hideFn, onToggle) => {
  switch (event.keyCode) {
    case ESCAPE_KEY:
      hideFn(onToggle);
      break;
    default:
      break;
  }
};

/**
 * Will hide the sidenav by toggling isOpen state or calling onProps in case of a controlled
 * element.
 */
const hide = (onToggle) => {
  onToggle(false);
};

const getConstraint = (position: string) => {
  if (position === "left") {
    return {
      right: 0,
    };
  }

  if (position === "right") {
    return {
      left: 0,
    };
  }

  if (position === "top") {
    return {
      bottom: 0,
    };
  }

  if (position === "bottom") {
    return {
      top: 0,
    };
  }
  return { right: 0 };
};

/**
 * A component that can be toggle on and off on a position defined by the user via props. Can be used for
 * sidebar navigation or mobile drop (show from bottom).
 */
export default function Drawer({
  children,
  height,
  maxWidth,
  full = false,
  isOpen = false,
  position = "left",
  allowDrag = true,
  onToggle,
}: DrawerProps) {
  const [activeTrap, setActiveTrap] = useState(false);

  // When the isOpen prop gets changed make sure to set or unset
  // mouse traps accordingly.
  useEffect(() => {
    if (isOpen) {
      setActiveTrap(true);
    } else {
      setActiveTrap(false);
    }
  }, [isOpen]);

  const sidenav = (
    <CustomMotion
      className={sideNavBlock({ position, full })}
      dragDirectionLock
      {...(allowDrag
        ? { drag: position === "left" || position === "right" ? "x" : "y" }
        : null)}
      dragElastic={0}
      data-testid="sidenav"
      onDragEnd={(event, info) => {
        if (position === "left" && info.point.x < 0) {
          hide(onToggle);
        }
        if (position === "right" && info.point.x > 0) {
          hide(onToggle);
        }
        if (position === "top" && info.point.y < 0) {
          hide(onToggle);
        }
        if (position === "bottom" && info.point.y > 0) {
          hide(onToggle);
        }
      }}
      onKeyDown={(e) => handleKeyDown(e, hide, onToggle)}
      initial="closed"
      animate={isOpen ? "open" : "closed"}
      {...(position && {
        variants: variants[position],
        dragConstraints: getConstraint(position),
      })}
      style={{
        height,
        maxWidth,
      }}
    >
      {isOpen && (
        <FocusTrap
          active={activeTrap}
          className="focus-area"
          focusTrapOptions={{
            clickOutsideDeactivates: true,
            escapeDeactivates: false,
            onDeactivate: () => setActiveTrap(false),
          }}
        >
          {children}
          <a href="#">
            <HiddenVisually>First targetable element</HiddenVisually>
          </a>
        </FocusTrap>
      )}
    </CustomMotion>
  );

  return (
    <div role="navigation">
      {sidenav}
      {isOpen && (
        <button
          className={styles.drawerOverlay}
          data-testid="sidenav-overlay"
          onClick={(e) => hide(onToggle)}
        />
      )}
    </div>
  );
}

export { Drawer };
